import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import DateOfBirthInput from "../inputs/DateOfBirthInput";
import TextInput from "../inputs/TextInput";
import { useSchoolFee } from "../../context/schoolFeeContext";
import { isValidDateOfBirth } from "../../utils/schoolFeeIdentity";

/**
 * Journey C's identity step: a student being registered, who does not have a
 * student number yet.
 *
 * ⛔ THERE IS NO MATRICULE INPUT IN THIS FILE, and that is the contract rather
 * than a layout choice. The server REJECTS a registration payment that carries
 * a matricule, so the field must not exist -- hiding one and leaving the value
 * in the journey would send it. Nothing here reads or writes
 * `schoolFeeContext.matricule`, which is what keeps the payload free of it.
 *
 * ⚠ Payer-declared, like journey B: this is what the payer says about the
 * student, not something the school has confirmed.
 */
const RegistrationIdentityForm = () => {
  const { t } = useTranslation();
  const {
    selectionId,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    dateOfBirth,
    setDateOfBirth,
  } = useSchoolFee();

  const dateInvalid = dateOfBirth !== "" && !isValidDateOfBirth(dateOfBirth);

  // ⛔ Nothing until the payer has browsed all the way to the class they are
  // registering into (ruled 2026-09-10), for the same reason as the manual
  // journey: the class comes first, and details entered before it are details
  // entered against a placement the payer has not chosen yet.
  if (selectionId.trim() === "") return null;

  return (
    <Box mb={4} data-testid="school-fee-registration-identity">
      <Typography variant="h6" fontWeight="bold" mb={1}>
        {t("schoolfee.registration.title")}
      </Typography>
      <Typography sx={{ fontSize: 13 }} mb={3}>
        {t("schoolfee.registration.hint")}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextInput
            fullWidth
            required
            type="text"
            id="schoolfee-registration-first-name"
            label={t("schoolfee.student.first_name")}
            value={firstName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFirstName(event.target.value)
            }
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextInput
            fullWidth
            required
            type="text"
            id="schoolfee-registration-last-name"
            label={t("schoolfee.student.last_name")}
            value={lastName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setLastName(event.target.value)
            }
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <DateOfBirthInput
            required
            id="schoolfee-registration-dob"
            label={t("schoolfee.student.date_of_birth")}
            value={dateOfBirth}
            onChange={setDateOfBirth}
            error={dateInvalid}
            helperText={
              dateInvalid
                ? t("schoolfee.identity.date_of_birth_invalid")
                : t("schoolfee.identity.date_of_birth_hint")
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default RegistrationIdentityForm;
